import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AppointmentDialog from "@/components/AppointmentDialog";

const SECTORS = ["Restauration", "Coiffure / Beauté", "Immigration", "Immobilier", "Santé / Clinique", "Services professionnels", "Commerce de détail", "Autre"];
const SIZES = ["1 (solo)", "2 à 5", "6 à 15", "16 à 50", "50+"];

const QUESTIONS = [
  { key: "secteur", label: "Quel est votre secteur d'activité ?", type: "select", options: SECTORS },
  { key: "employes", label: "Combien d'employés avez-vous ?", type: "select", options: SIZES },
  { key: "siteWeb", label: "Possédez-vous un site web professionnel ?", type: "radio", options: [
    { v: "Oui, moderne", points: 15 },
    { v: "Oui, mais ancien", points: 8 },
    { v: "Non", points: 0 },
  ]},
  { key: "googleBusiness", label: "Utilisez-vous Google Business Profile ?", type: "radio", options: [
    { v: "Oui, optimisé", points: 15 },
    { v: "Oui, basique", points: 7 },
    { v: "Non", points: 0 },
  ]},
  { key: "publicite", label: "Faites-vous de la publicité numérique (Facebook, Google Ads) ?", type: "radio", options: [
    { v: "Oui, régulièrement", points: 20 },
    { v: "Parfois", points: 10 },
    { v: "Non", points: 0 },
  ]},
  { key: "ia", label: "Utilisez-vous l'IA ou l'automatisation dans votre entreprise ?", type: "radio", options: [
    { v: "Oui, plusieurs outils", points: 25 },
    { v: "Un peu", points: 12 },
    { v: "Pas du tout", points: 0 },
  ]},
];

export default function Diagnostic() {
  const [step, setStep] = useState(0); // 0 = intro, 1..6 = questions, 7 = lead, 8 = result
  const [answers, setAnswers] = useState({});
  const [lead, setLead] = useState({ nom: "", courriel: "" });
  const [score, setScore] = useState(0);
  const [bookOpen, setBookOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const totalQ = QUESTIONS.length;
  const calculateScore = (a) => {
    let s = 10; // base score for completing
    QUESTIONS.forEach((q) => {
      if (q.type === "radio") {
        const found = q.options.find((o) => o.v === a[q.key]);
        if (found) s += found.points;
      } else {
        if (a[q.key]) s += 5;
      }
    });
    return Math.min(100, s);
  };

  const submit = async () => {
    const s = calculateScore(answers);
    setScore(s);
    setSubmitting(true);
    try {
      await api.post("/diagnostic", { ...answers, score: s, ...lead });
      toast.success("Diagnostic enregistré !");
    } catch { /* keep UX flowing */ }
    setSubmitting(false);
    setStep(totalQ + 2);
  };

  const next = () => {
    if (step >= 1 && step <= totalQ) {
      const q = QUESTIONS[step - 1];
      if (!answers[q.key]) {
        toast.error("Veuillez répondre avant de continuer.");
        return;
      }
    }
    setStep(step + 1);
  };

  const currentQ = step >= 1 && step <= totalQ ? QUESTIONS[step - 1] : null;
  const progress = step === 0 ? 0 : Math.min(100, (step / (totalQ + 2)) * 100);

  return (
    <section id="diagnostic" className="relative py-24 bg-[#0B1F3A] overflow-hidden" data-testid="diagnostic-section">
      <div className="absolute inset-0 bg-dot opacity-30 pointer-events-none" />
      <div className="absolute -top-32 right-1/4 h-[400px] w-[400px] rounded-full bg-[#00B8D9]/15 blur-3xl" />

      <div className="relative mx-auto max-w-4xl px-6 md:px-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00B8D9]/15 border border-[#00B8D9]/30 text-[#00B8D9] text-xs font-bold tracking-[0.18em] uppercase mb-5">
            <Sparkles className="h-3.5 w-3.5" />
            Diagnostic interactif · 2 minutes
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Évaluez votre maturité numérique
          </h2>
          <p className="mt-3 text-gray-300 max-w-2xl mx-auto">
            Répondez à 6 questions et obtenez votre score personnalisé + recommandations gratuites.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10">
          {step > 0 && step <= totalQ + 1 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2 text-sm">
                <span className="text-[#4B5563] font-medium">Étape {Math.min(step, totalQ + 1)} / {totalQ + 1}</span>
                <span className="text-[#00B8D9] font-bold">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-6">
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B1F3A] mb-4">Prêt à découvrir votre score ?</h3>
                <p className="text-[#4B5563] mb-8 max-w-lg mx-auto">
                  En 2 minutes, identifiez vos opportunités d'amélioration et recevez un plan d'action sur mesure.
                </p>
                <Button onClick={() => setStep(1)} data-testid="diagnostic-start" className="bg-[#00B8D9] hover:bg-[#0096B3] text-white px-8 py-6 text-base">
                  Démarrer le diagnostic <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            )}

            {currentQ && (
              <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1F3A]">{currentQ.label}</h3>

                {currentQ.type === "select" ? (
                  <Select value={answers[currentQ.key] || ""} onValueChange={(v) => setAnswers({ ...answers, [currentQ.key]: v })}>
                    <SelectTrigger data-testid={`diag-q-${currentQ.key}`} className="h-12 text-base"><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                    <SelectContent>{currentQ.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <RadioGroup value={answers[currentQ.key] || ""} onValueChange={(v) => setAnswers({ ...answers, [currentQ.key]: v })}>
                    {currentQ.options.map((o) => (
                      <label key={o.v} className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${answers[currentQ.key] === o.v ? "border-[#00B8D9] bg-[#00B8D9]/5" : "border-gray-200 hover:border-gray-300"}`}>
                        <RadioGroupItem value={o.v} data-testid={`diag-opt-${currentQ.key}-${o.v}`} />
                        <span className="font-medium text-[#0B1F3A]">{o.v}</span>
                      </label>
                    ))}
                  </RadioGroup>
                )}

                <div className="flex gap-2 pt-2">
                  {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1" data-testid="diag-back">Retour</Button>}
                  <Button onClick={next} className="flex-1 bg-[#00B8D9] hover:bg-[#0096B3]" data-testid="diag-next">
                    {step === totalQ ? "Voir mon score" : "Continuer"} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === totalQ + 1 && (
              <motion.div key="lead" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1F3A]">Recevez votre résultat par courriel</h3>
                <p className="text-sm text-[#4B5563]">Optionnel — vous verrez le score à l'écran. Donnez votre courriel pour recevoir le plan d'action détaillé.</p>
                <div><Label>Nom</Label><Input data-testid="diag-lead-nom" value={lead.nom} onChange={(e) => setLead({ ...lead, nom: e.target.value })} /></div>
                <div><Label>Courriel</Label><Input data-testid="diag-lead-email" type="email" value={lead.courriel} onChange={(e) => setLead({ ...lead, courriel: e.target.value })} /></div>
                <Button onClick={submit} disabled={submitting} className="w-full bg-[#00B8D9] hover:bg-[#0096B3] py-6" data-testid="diag-submit">
                  {submitting ? "Calcul..." : "Obtenir mon score"}
                </Button>
              </motion.div>
            )}

            {step === totalQ + 2 && (
              <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
                <CheckCircle2 className="h-14 w-14 text-[#00B8D9] mx-auto mb-3" />
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#00B8D9] mb-2">Votre score</div>
                <div className="font-heading font-black text-7xl sm:text-8xl text-[#0B1F3A]" data-testid="diag-score">
                  {score}<span className="text-3xl text-[#4B5563]">/100</span>
                </div>
                <p className="mt-3 text-[#4B5563] max-w-md mx-auto">
                  {score < 40 && "Votre potentiel de croissance numérique est immense. Ne perdez plus de temps."}
                  {score >= 40 && score < 70 && "Bonne base, mais d'importantes opportunités restent à saisir."}
                  {score >= 70 && "Excellent ! Optimisons les derniers points pour passer au niveau supérieur."}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => setBookOpen(true)} className="bg-[#00B8D9] hover:bg-[#0096B3] text-white px-6 py-6" data-testid="diag-book-cta">
                    Réserver une consultation gratuite
                  </Button>
                  <Button variant="outline" onClick={() => { setStep(0); setAnswers({}); setLead({ nom: "", courriel: "" }); setScore(0); }} className="border-[#0B1F3A]" data-testid="diag-restart">
                    Refaire le diagnostic
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <AppointmentDialog open={bookOpen} onOpenChange={setBookOpen} defaultType="decouverte" />
    </section>
  );
}
